import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useRevokeCandidateConsent } from '@/hooks/use-candidates';
import { formatDate } from '@/lib/date';
import { cn } from '@/lib/utils';
import type { CandidateDetail, ConsentTimeline } from '@/types/candidates';
import { Shield } from 'lucide-react';
import { useState } from 'react';
import {
  CONSENT_SOURCE_LABEL,
  GDPR_STATUS_LABEL,
  gdprStatusTone,
} from '../lib/consent-labels';
import { consentCardActions } from '../lib/consent-actions';
import { consentExpiryText, consentExpiryTone, renewalRequestedAt } from '../lib/consent-expiry';
import { CandidateGdprAnonymiseDialog } from './candidate-gdpr-anonymise';
import { CandidateGdprConsentRequest } from './candidate-gdpr-consent-request';

interface CandidateGdprStatusCardProps {
  candidate: Pick<CandidateDetail, 'id' | 'fullName' | 'email' | 'status' | 'employeeId'>;
  timeline: ConsentTimeline;
}

export function CandidateGdprStatusCard({ candidate, timeline }: CandidateGdprStatusCardProps) {
  const candidateId = candidate.id;
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [note, setNote] = useState('');
  const [anonymiseOpen, setAnonymiseOpen] = useState(false);
  // HW-1055: the "Anonymise now?" popup right after a revoke. Kept apart from
  // `anonymiseOpen` so the wording does not flip while the dialog closes.
  const [afterRevoke, setAfterRevoke] = useState(false);
  const revoke = useRevokeCandidateConsent(candidateId);

  const openAnonymise = (fromRevoke: boolean) => {
    setAfterRevoke(fromRevoke);
    setAnonymiseOpen(true);
  };

  const handleRevoke = async () => {
    await revoke.mutateAsync({ note: note.trim() });
    setRevokeOpen(false);
    setNote('');
    openAnonymise(true);
  };

  // HW-1055: the actions follow the status, so none of them ends in a refusal.
  const actions = consentCardActions({
    gdprStatus: timeline.status,
    status: candidate.status,
    employeeId: candidate.employeeId,
  });
  const renewalSentOn = renewalRequestedAt(timeline);

  return (
    <>
      <section className="rounded-lg border border-border bg-background p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                gdprStatusTone(timeline.status),
              )}
            >
              <Shield className="h-5 w-5" />
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'rounded-md px-2 py-0.5 text-xs font-medium uppercase tracking-wide',
                    gdprStatusTone(timeline.status),
                  )}
                >
                  {GDPR_STATUS_LABEL[timeline.status]}
                </span>
                {timeline.policyVersion ? (
                  <span className="text-xs text-muted-foreground">
                    Policy {timeline.policyVersion}
                  </span>
                ) : null}
              </div>
              <div className="text-sm text-foreground">
                {timeline.grantedAt
                  ? // HW-1023: the UTC day, the day the consent's year is counted from.
                    `Granted on ${formatDate(timeline.grantedAt, 'short')}${
                      timeline.grantedSource
                        ? ` via ${CONSENT_SOURCE_LABEL[timeline.grantedSource]}`
                        : ''
                    }`
                  : timeline.revokedAt
                    ? // HW-1057: the candidate withdrew it from the email link.
                      timeline.revokedSource === 'EMAIL_LINK'
                      ? `Withdrawn by the candidate on ${formatDate(timeline.revokedAt, 'short')}`
                      : `Revoked on ${formatDate(timeline.revokedAt, 'short')}`
                    : timeline.lastRequestedAt
                      ? `Requested on ${formatDate(timeline.lastRequestedAt, 'short')} — awaiting response`
                      : 'Consent has not been requested yet.'}
              </div>
              {renewalSentOn ? (
                <div className="text-xs text-muted-foreground">
                  Renewal email sent on {formatDate(renewalSentOn, 'short')} — awaiting response
                </div>
              ) : null}
              {timeline.status === 'REVOKED' && actions.anonymise ? (
                <div className="text-xs text-destructive">
                  The candidate is not asked for consent again. Anonymise them to delete them and
                  all their data (GDPR Art. 17).
                </div>
              ) : null}
              {/* HW-1023: when the consent ends; once ended, the way to renew it. */}
              {timeline.consentExpiry ? (
                <div className={cn('text-xs', consentExpiryTone(timeline.consentExpiry))}>
                  {consentExpiryText(timeline.consentExpiry)}
                  {timeline.consentExpiry.state === 'EXPIRED'
                    ? ' — send a new consent email or record offline consent to renew it.'
                    : ''}
                </div>
              ) : null}
            </div>
          </div>

          {actions.revoke ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => setRevokeOpen(true)}
            >
              Revoke
            </Button>
          ) : null}
          {actions.anonymise ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0 border-destructive/60 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => openAnonymise(false)}
            >
              Anonymise candidate
            </Button>
          ) : null}
        </div>
        {actions.request ? (
          <div className="mt-4">
            <CandidateGdprConsentRequest
              candidateId={candidateId}
              hasEmail={!!candidate.email}
              lastRequestedAt={timeline.lastRequestedAt}
            />
          </div>
        ) : null}
      </section>

      <CandidateGdprAnonymiseDialog
        candidateId={candidateId}
        candidateName={candidate.fullName}
        open={anonymiseOpen}
        onOpenChange={setAnonymiseOpen}
        afterRevoke={afterRevoke}
      />

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke consent?</AlertDialogTitle>
            <AlertDialogDescription>
              Marks consent as revoked. The candidate is not asked for consent again and is not
              moved through vacancies; next, their data is anonymised.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            {/* HW-1057: required — the candidate decides; this records how they asked. */}
            <label htmlFor="revoke-note" className="text-xs font-medium text-foreground">
              How did the candidate ask to withdraw?
            </label>
            <Textarea
              id="revoke-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Asked by email on 28.10 to stop using their data"
              maxLength={1000}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              disabled={revoke.isPending || !note.trim()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revoke.isPending ? 'Revoking…' : 'Revoke consent'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
